
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Copy, FileText, Package, AlertTriangle, CheckCircle, Utensils, Droplets, Sparkles, Archive, Wheat, Apple, Milk, Fish, Beef, Soup, Coffee, Wine, ShoppingBasket, Cookie, Salad, Pizza } from 'lucide-react';
import { ArticleInventaire } from '@/hooks/useInventaire';
import { toast } from '@/components/ui/use-toast';

interface ListeAchatProps {
  articles: ArticleInventaire[];
  nomBateau: string;
}

const ListeAchat = ({ articles, nomBateau }: ListeAchatProps) => {
  const articlesCritiques = articles.filter(article => {
    const pourcentage = article.quantite_max > 0 ? (article.quantite / article.quantite_max) * 100 : 0;
    return pourcentage < 30;
  });
  
  const autresArticles = articles.filter(article => {
    const pourcentage = article.quantite_max > 0 ? (article.quantite / article.quantite_max) * 100 : 0;
    return pourcentage >= 30 && pourcentage < 100;
  });

  const getSousCategorieIcon = (sousCategorie: string | null) => {
    if (!sousCategorie) return Package;
    
    switch (sousCategorie.toLowerCase()) {
      case 'conserves':
        return Archive;
      case 'pâtes':
      case 'riz':
      case 'céréales':
      case 'pain et céréales':
        return Wheat;
      case 'fruits frais':
      case 'légumes frais':
        return Apple;
      case 'produits laitiers':
        return Milk;
      case 'fromages':
        return ShoppingBasket;
      case 'boissons':
      case 'eau':
        return Droplets;
      case 'viandes':
      case 'charcuterie':
        return Beef;
      case 'poissons':
      case 'fruits de mer':
        return Fish;
      case 'soupes':
      case 'plats préparés':
        return Soup;
      case 'café':
      case 'thé':
        return Coffee;
      case 'alcools':
      case 'vins':
        return Wine;
      case 'boulangerie':
      case 'pain':
        return ShoppingBasket;
      case 'confiseries':
      case 'chocolats':
        return Package;
      case 'biscuits':
      case 'gâteaux':
        return Cookie;
      case 'condiments':
      case 'épices':
        return Salad;
      case 'pizza':
        return Pizza;
      default:
        return Package;
    }
  };

  const genererListeAchat = () => {
    const listeAchat: string[] = [];

    // Priorité aux articles critiques
    articlesCritiques.forEach(article => {
      const manquant = article.quantite_max - article.quantite;
      listeAchat.push(`${manquant} ${article.unite || 'unités'} - ${article.nom} (CRITIQUE)`);
    });

    // Ensuite les autres articles pour atteindre 100%
    autresArticles.forEach(article => {
      const manquant = article.quantite_max - article.quantite;
      if (manquant > 0) {
        listeAchat.push(`${manquant} ${article.unite || 'unités'} - ${article.nom}`);
      }
    });

    return listeAchat;
  };

  const copierListeAchat = () => {
    const liste = genererListeAchat();
    if (liste.length === 0) {
      toast({
        title: "Information",
        description: "Aucun article à acheter. Votre inventaire est complet !",
      });
      return;
    }

    const texte = "Liste d'achat:\n" + liste.map(item => `• ${item}`).join('\n');
    navigator.clipboard.writeText(texte).then(() => {
      toast({
        title: "Succès",
        description: "Liste d'achat copiée dans le presse-papiers !",
      });
    });
  };

  const genererPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Configuration de couleurs nautic - COULEURS EXACTES DU SITE
      const nauticBlue = [20, 26, 51] as const; // #141A33
      const nauticGold = [232, 163, 6] as const; // #E8A306
      const darkGray = [75, 85, 99] as const;
      const checkboxSize = 3.5;
      
      // En-tête avec fond bleu nautic
      doc.setFillColor(nauticBlue[0], nauticBlue[1], nauticBlue[2]);
      doc.rect(0, 0, 210, 45, 'F');
      
      // Texte en or nautic
      doc.setTextColor(nauticGold[0], nauticGold[1], nauticGold[2]);
      doc.setFontSize(22);
      doc.text('LISTE D\'ACHAT', 20, 18);
      
      // Nom du navire
      doc.setFontSize(16);
      doc.text(nomBateau || 'Inventaire général', 20, 30);
      
      // Site en petit
      doc.setFontSize(9);
      doc.text('Créé par nauticoach.com', 20, 40);
      
      // Informations à droite
      doc.setFontSize(10);
      doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 150, 18);
      const totalArticles = articlesCritiques.length + autresArticles.filter(a => a.quantite_max - a.quantite > 0).length;
      doc.text(`${totalArticles} articles`, 150, 28);
      
      // Fonction pour dessiner une checkbox
      const drawCheckbox = (x: number, y: number) => {
        doc.setDrawColor(nauticBlue[0], nauticBlue[1], nauticBlue[2]);
        doc.setLineWidth(0.3);
        doc.rect(x, y - 3, checkboxSize, checkboxSize);
      };
      
      // Ordre des catégories principales
      const ordreCategories = ['alimentation', 'boissons', 'hygiène', 'hygiene'];
      
      // Fonction pour obtenir la catégorie principale
      const getCategorieOrdre = (categorie: string): number => {
        const cat = categorie.toLowerCase();
        for (let i = 0; i < ordreCategories.length; i++) {
          if (cat.includes(ordreCategories[i])) return i;
        }
        return 99; // Autres catégories à la fin
      };
      
      // Combiner tous les articles à acheter
      const tousArticles = [
        ...articlesCritiques.map(a => ({ ...a, isCritique: true })),
        ...autresArticles.filter(a => a.quantite_max - a.quantite > 0).map(a => ({ ...a, isCritique: false }))
      ];
      
      // Grouper par catégorie principale puis sous-catégorie
      const articlesParCategorie = tousArticles.reduce((acc, article) => {
        const categorie = article.categorie || 'Autres';
        if (!acc[categorie]) {
          acc[categorie] = {};
        }
        const sousCategorie = article.sous_categorie || 'Général';
        if (!acc[categorie][sousCategorie]) {
          acc[categorie][sousCategorie] = [];
        }
        acc[categorie][sousCategorie].push(article);
        return acc;
      }, {} as Record<string, Record<string, (ArticleInventaire & { isCritique: boolean })[]>>);
      
      // Trier les catégories selon l'ordre défini
      const categoriesTriees = Object.keys(articlesParCategorie).sort((a, b) => {
        return getCategorieOrdre(a) - getCategorieOrdre(b);
      });
      
      let yPosition = 55;
      const colWidth = 95;
      const leftMargin = 10;
      const rightMargin = 110;
      let currentCol = 0;
      let leftY = yPosition;
      let rightY = yPosition;
      
      // Afficher les catégories sur deux colonnes
      categoriesTriees.forEach((categorie) => {
        const sousCategories = articlesParCategorie[categorie];
        const xStart = currentCol === 0 ? leftMargin : rightMargin;
        let currentY = currentCol === 0 ? leftY : rightY;
        
        // Vérifier si on a besoin d'une nouvelle page
        if (currentY > 260) {
          doc.addPage();
          leftY = 20;
          rightY = 20;
          currentY = 20;
        }
        
        // Titre de la catégorie
        doc.setFillColor(nauticBlue[0], nauticBlue[1], nauticBlue[2]);
        doc.rect(xStart, currentY - 4, colWidth - 5, 10, 'F');
        doc.setTextColor(nauticGold[0], nauticGold[1], nauticGold[2]);
        doc.setFontSize(11);
        doc.text(categorie.toUpperCase(), xStart + 3, currentY + 3);
        currentY += 12;
        
        // Sous-catégories et articles
        Object.entries(sousCategories).forEach(([sousCategorie, articles]) => {
          if (currentY > 270) {
            doc.addPage();
            currentY = 20;
          }
          
          // Titre sous-catégorie
          doc.setTextColor(nauticGold[0], nauticGold[1], nauticGold[2]);
          doc.setFontSize(9);
          doc.text(`• ${sousCategorie}`, xStart + 2, currentY);
          currentY += 5;
          
          // Articles
          doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
          doc.setFontSize(8);
          
          articles.forEach((article) => {
            if (currentY > 275) {
              doc.addPage();
              currentY = 20;
            }
            
            const manquant = article.quantite_max - article.quantite;
            drawCheckbox(xStart + 3, currentY);
            
            const texte = `${manquant} ${article.unite || 'u.'} - ${article.nom}`;
            const texteCoupe = texte.length > 35 ? texte.substring(0, 32) + '...' : texte;
            
            if (article.isCritique) {
              doc.setTextColor(220, 38, 38); // Rouge pour critique
            } else {
              doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
            }
            
            doc.text(texteCoupe, xStart + 9, currentY);
            currentY += 5;
          });
          
          currentY += 2;
        });
        
        // Mettre à jour la position Y pour la colonne
        if (currentCol === 0) {
          leftY = currentY + 5;
          currentCol = 1;
        } else {
          rightY = currentY + 5;
          currentCol = 0;
          // Synchroniser les hauteurs pour la prochaine ligne
          const maxY = Math.max(leftY, rightY);
          leftY = maxY;
          rightY = maxY;
        }
      });
      
      // Pied de page
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(nauticBlue[0], nauticBlue[1], nauticBlue[2]);
        doc.rect(0, 287, 210, 10, 'F');
        doc.setTextColor(nauticGold[0], nauticGold[1], nauticGold[2]);
        doc.setFontSize(8);
        doc.text('Créé par nauticoach.com', 105, 293, { align: 'center' });
        doc.text(`${i}/${pageCount}`, 195, 293);
      }
      
      // Télécharger le PDF
      doc.save(`liste-achat-${nomBateau.replace(/\s+/g, '-').toLowerCase() || 'inventaire'}.pdf`);
      
      toast({
        title: "Succès",
        description: "Liste d'achat PDF générée avec succès !",
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      toast({
        title: "Erreur",
        description: "Impossible de générer le PDF.",
        variant: "destructive",
      });
    }
  };

  const liste = genererListeAchat();

  if (liste.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Liste d'achat
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-green-700 mb-2">Inventaire complet !</h3>
              <p className="text-gray-500">
                Tous vos articles sont à niveau. Aucun achat nécessaire.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Organiser les articles par sous-catégorie pour l'affichage
  const articlesCritiquesParSousCategorie = articlesCritiques.reduce((acc, article) => {
    const sousCategorie = article.sous_categorie || 'Sans sous-catégorie';
    if (!acc[sousCategorie]) {
      acc[sousCategorie] = [];
    }
    acc[sousCategorie].push(article);
    return acc;
  }, {} as Record<string, ArticleInventaire[]>);

  const autresArticlesParSousCategorie = autresArticles.filter(a => a.quantite_max - a.quantite > 0).reduce((acc, article) => {
    const sousCategorie = article.sous_categorie || 'Sans sous-catégorie';
    if (!acc[sousCategorie]) {
      acc[sousCategorie] = [];
    }
    acc[sousCategorie].push(article);
    return acc;
  }, {} as Record<string, ArticleInventaire[]>);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-nautic" />
            Liste d'achat ({liste.length} articles)
          </CardTitle>
          <div className="flex gap-2">
            <Button onClick={copierListeAchat} variant="outline" size="sm">
              <Copy className="h-4 w-4 mr-2" />
              Copier
            </Button>
            <Button onClick={genererPDF} className="bg-nautic hover:bg-nautic/90 text-white" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              Créer liste d'achat en PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.keys(articlesCritiquesParSousCategorie).length > 0 && (
              <div>
                <h4 className="font-medium text-red-600 mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Articles critiques (priorité haute)
                </h4>
                {Object.entries(articlesCritiquesParSousCategorie).map(([sousCategorie, articlesCategorie]) => {
                  const SousCategorieIcon = getSousCategorieIcon(sousCategorie);
                  return (
                    <div key={sousCategorie} className="mb-4">
                      <h5 className="font-medium text-nautic mb-2 flex items-center gap-2">
                        <SousCategorieIcon className="h-4 w-4" />
                        {sousCategorie}
                      </h5>
                      {articlesCategorie.map(article => {
                        const manquant = article.quantite_max - article.quantite;
                        return (
                          <div key={article.id} className="flex justify-between items-center p-2 bg-red-50 rounded border-l-4 border-red-500 mb-2">
                            <span className="font-medium">{article.nom}</span>
                            <span className="text-red-600 font-medium">{manquant} {article.unite || 'unités'}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            {Object.keys(autresArticlesParSousCategorie).length > 0 && (
              <div>
                <h4 className="font-medium text-nautic mb-4 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Autres articles à compléter
                </h4>
                {Object.entries(autresArticlesParSousCategorie).map(([sousCategorie, articlesCategorie]) => {
                  const SousCategorieIcon = getSousCategorieIcon(sousCategorie);
                  return (
                    <div key={sousCategorie} className="mb-4">
                      <h5 className="font-medium text-nautic mb-2 flex items-center gap-2">
                        <SousCategorieIcon className="h-4 w-4" />
                        {sousCategorie}
                      </h5>
                      {articlesCategorie.map(article => {
                        const manquant = article.quantite_max - article.quantite;
                        return (
                          <div key={article.id} className="flex justify-between items-center p-2 bg-nautic/5 rounded border-l-4 border-nautic mb-2">
                            <span>{article.nom}</span>
                            <span className="text-nautic font-medium">{manquant} {article.unite || 'unités'}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ListeAchat;
